// ---------- les partages, pour le badge « Partage » ----------
// Le serveur compte les feuilles DIFFÉRENTES partagées (backend/src/routes/account.ts, POST
// /me/shares/:puzzleId) ; ce module ne fait que les lui faire parvenir, sans en perdre.
//
// POURQUOI UNE FILE ET PAS UN SIMPLE APPEL. Le partage se propose sur la carte de fin, à l'instant
// même où la sauvegarde qui marque la feuille FINIE part vers le serveur — et le serveur refuse
// (409) un partage dont il ne connaît pas encore la victoire. Sans réseau, ou sans compte encore
// créé, l'appel échouerait aussi. Dans tous ces cas le partage reste ici, et repart au prochain
// passage de la synchronisation (sync.ts, run()) : il finit toujours par arriver.

import { currentUser } from './account';
import { postCreationShare, postShare } from './api';

const KEY = 'codigrid:shares:v1';
/** Garde-fou, pas une limite qu'on atteindra : un partage en attente sort de la file dès que le
 *  serveur l'a, et il n'y en a qu'un par feuille. Sans plafond, un serveur qui refuserait pour
 *  toujours (une feuille supprimée entre-temps) ferait grossir la file sans fin. */
const MAX_PENDING = 200;

function readPending(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '[]') as unknown;
    return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function writePending(ids: readonly string[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(ids.slice(-MAX_PENDING)));
  } catch {
    /* quota plein ou mode privé : le partage est perdu pour le badge, pas pour le joueur */
  }
}

/** Le joueur vient de partager cette feuille (le sélecteur a rendu la main sur un choix, ou le
 *  lien est parti au presse-papiers). Mis en file AVANT l'envoi : si l'application se ferme
 *  pendant la requête, le partage repartira au prochain lancement. */
export function noteShare(puzzleId: string): void {
  const pending = readPending();
  if (!pending.includes(puzzleId)) writePending([...pending, puzzleId]);
  void flushShares();
}

/**
 * Le joueur vient de partager une de SES créations : un signal pour le compteur du jour de
 * l'atelier, et rien de plus (2026-10-17) — une création ne compte pas pour le badge.
 *
 * Sans file, à la différence de noteShare : ce n'est qu'une statistique. Un partage fait hors
 * ligne, ou sans compte, n'est simplement pas compté ; le garder pour plus tard aurait demandé une
 * seconde file à vider, pour un chiffre qui n'a pas besoin d'être exact à l'unité.
 */
export function noteCreationShare(id: string): void {
  void (async () => {
    const token = await currentUser();
    if (token) await postCreationShare(token, id);
  })().catch(() => {
    /* hors ligne : ce partage-là ne sera pas compté */
  });
}

let flushing: Promise<void> | null = null;

/** Envoie ce qui attend. Appelée après chaque partage et à la fin de chaque passage de la
 *  synchronisation — c'est-à-dire une fois les victoires poussées, quand un 409 n'a plus de
 *  raison d'être. Un seul envoi à la fois : deux appels rapprochés ne doublent pas les requêtes. */
export function flushShares(): Promise<void> {
  return (flushing ??= run().finally(() => {
    flushing = null;
  }));
}

async function run(): Promise<void> {
  const token = await currentUser();
  if (!token) return; // pas de compte : rien à déclarer pour l'instant, la file attend
  for (const id of readPending()) {
    try {
      if ((await postShare(token, id)) === 'later') continue; // victoire pas encore arrivée
    } catch {
      return; // hors ligne, ou le compte a changé : on retentera au prochain passage
    }
    writePending(readPending().filter((p) => p !== id));
  }
}
